import { useMemo } from 'react';
import { useApi } from '../infrastructure/useApi';
import { DEFAULT_API_OPTIONS } from '../infrastructure/defaults';
import { API_URLS } from '../apiUrls';
import { API_METHODS } from '../constants/apiMethods';

/**
 * Hook for accessing Products API methods.
 *
 * Usage:
 * const { getProductList, downloadPrintCard, cancel } = useProductsApi();
 * await getProductList(params);
 *
 * Cancellation scope:
 * - cancel() applies to requests started by this hook instance.
 * - For independent cancellation scopes, create separate hook instances.
 */
export const useProductsApi = (baseOptions = DEFAULT_API_OPTIONS) => {
	const { call, cancel } = useApi(baseOptions);

	return useMemo(
		() => ({
			getDetails: ({ id }) =>
				call({
					url: API_URLS.product.getProductDetails({ productId: id }),
					method: API_METHODS.GET,
				}),

			getProductList: ({ limit, page, UserID, sort, filters, ...params }) =>
				call({
					url: API_URLS.product.getProductList(),
					method: API_METHODS.POST,
					payload: {
						Page_Number: page + 1,
						Page_Size: limit,
						UserID,
						Order_By: sort,
						Filters:
							filters?.category || filters?.plant
								? {
										Product_Category: filters?.category,
										Plant_Code: filters?.plant,
								  }
								: undefined,
						...params,
					},
				}),

			downloadProductList: (format, payload) =>
				call({
					url: API_URLS.product.downloadProductList(format),
					method: API_METHODS.POST_BLOB,
					payload,
				}),

			downloadPrintCard: (productId) =>
				call({
					url: API_URLS.product.downloadPrintCard(productId),
					method: API_METHODS.GET_BLOB,
				}),

			cancel,
		}),
		[call, cancel]
	);
};
